// IST-authoritative canonical half-hour pickup-slot helpers for STANDARD Sender NewBooking.
// Convenience/UX only — the backend (accept-quote) is the authoritative validator. All slot
// values are canonical "HH:MM" with minutes in {00, 30}. Times are derived in Asia/Kolkata (IST)
// deterministically via Intl so the browser's local/UTC clock is never the service-date authority.

const PAD = (n) => String(n).padStart(2, "0");
const MAX_SLOT_MIN = 23 * 60 + 30; // 23:30

// All 48 canonical half-hour slots "00:00" .. "23:30".
export function allSlots() {
  const out = [];
  for (let h = 0; h < 24; h++) {
    out.push(`${PAD(h)}:00`);
    out.push(`${PAD(h)}:30`);
  }
  return out;
}

const slotToMinutes = (s) => {
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};

// IST parts for a given Date (defaults to real now).
export function istParts(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(now);
  const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  const hour = parseInt(p.hour, 10) % 24; // guard "24" at midnight on some engines
  const minute = parseInt(p.minute, 10);
  const second = parseInt(p.second, 10);
  return {
    date: `${p.year}-${p.month}-${p.day}`,
    hour, minute, second,
    totalSeconds: hour * 3600 + minute * 60 + second,
  };
}

// First same-day slot STRICTLY after now (minutes-from-midnight); null if none remain.
export function nextSameDaySlotMinutes(ist) {
  const next = (Math.floor(ist.totalSeconds / 1800) + 1) * 30;
  return next > MAX_SLOT_MIN ? null : next;
}

// IST "today" as YYYY-MM-DD.
export function istToday(now = new Date()) {
  return istParts(now).date;
}

// Valid canonical slots for a service date (YYYY-MM-DD) given IST now.
export function slotsForDate(serviceDate, now = new Date()) {
  if (!serviceDate) return [];
  const ist = istParts(now);
  if (serviceDate < ist.date) return []; // past date
  if (serviceDate > ist.date) return allSlots(); // future date
  const next = nextSameDaySlotMinutes(ist); // today
  if (next == null) return [];
  return allSlots().filter((s) => slotToMinutes(s) >= next);
}

export function isSlotValid(serviceDate, value, now = new Date()) {
  return !!value && slotsForDate(serviceDate, now).includes(value);
}

// Minimum selectable service date (YYYY-MM-DD) in IST: today if same-day slots remain, else tomorrow.
export function minServiceDate(now = new Date()) {
  const ist = istParts(now);
  if (nextSameDaySlotMinutes(ist) != null) return ist.date;
  const d = new Date(`${ist.date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
